import { IconButton, Menu, MenuItem, Tooltip, type MenuItemProps } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";

import { useId, useRef, useState, type FC, type ReactNode } from "react";
import { MoreHorizontalIcon, TrashCanIcon } from "./icons";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { useMutation } from "@tanstack/react-query";

type ActionMenuProps = {
  children: ReactNode;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onTriggerHover?: () => void;
  ariaLabelKey?: TranslationKey;
};
export const ActionMenu: FC<ActionMenuProps> = ({
  children,
  isOpen,
  onOpenChange,
  onTriggerHover,
  ariaLabelKey = "ActionMenu.ButtonLabel",
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const menuId = useId();
  const buttonId = useId();
  return (
    <nav>
      <IconButton
        onMouseEnter={onTriggerHover}
        onFocusVisible={onTriggerHover}
        ref={buttonRef}
        aria-controls={isOpen ? menuId : undefined}
        aria-haspopup="true"
        sx={{ mixBlendMode: "multiply" }}
        aria-expanded={isOpen ? "true" : undefined}
        aria-label={t(ariaLabelKey)}
        onClick={() => onOpenChange(true)}
      >
        <MoreHorizontalIcon size={20} color={theme => theme.palette.text.primary} />
      </IconButton>
      <Menu
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        id={menuId}
        anchorEl={buttonRef.current}
        open={isOpen}
        onClose={() => onOpenChange(false)}
        aria-labelledby={buttonId}
      >
        {children}
      </Menu>
    </nav>
  );
};

type ActionMenuItemWithPermissionProps = {
  missingPermission?: boolean | string | undefined;
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  /** Fires on mouse enter and on focus, so keyboard users get it too. */
  onHover?: () => void;
};

export const ActionMenuItemWithPermission = ({
  missingPermission,
  children,
  disabled,
  onClick,
  onHover,
  ...props
}: ActionMenuItemWithPermissionProps & MenuItemProps) => {
  const { t } = useTranslation();

  if (missingPermission) {
    const missingPermissionTooltipText =
      typeof missingPermission === "string" ? missingPermission : t("ActionMenu.Item.MissingPermission");
    return (
      <Tooltip enterDelay={300} title={missingPermissionTooltipText} followCursor>
        <span>
          <MenuItem disabled>{children}</MenuItem>
        </span>
      </Tooltip>
    );
  }

  return (
    <MenuItem
      {...props}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
    >
      {children}
    </MenuItem>
  );
};

type ActionMenuDeleteMenuItemProps = {
  onDelete: () => Promise<unknown>;
  onDeleteSuccess?: () => void;
  onClose: () => void;
  disabled?: boolean;
  confirmDeleteDialog: { header: string; message: ReactNode; delete?: string; errorMessage?: string };
  missingPermission?: ActionMenuItemWithPermissionProps["missingPermission"];
  variant?: "standard" | "severe";
  label?: string;
};

export const ActionMenuDeleteMenuItem: FC<ActionMenuDeleteMenuItemProps> = ({
  onDelete,
  onDeleteSuccess,
  onClose,
  disabled,
  missingPermission,
  confirmDeleteDialog,
  variant,
  label,
}) => {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { mutateAsync } = useMutation({ mutationFn: onDelete });

  const handleClose = () => {
    setIsConfirmOpen(false);
    onClose();
  };

  const handleConfirmDelete = () => mutateAsync(undefined, { onSuccess: onDeleteSuccess });

  return (
    <>
      <ActionMenuItemWithPermission
        disabled={disabled}
        missingPermission={missingPermission}
        onClick={() => setIsConfirmOpen(true)}
      >
        <TrashCanIcon />
        {label ?? t("GridDrawer.Menu.DeleteItem")}
      </ActionMenuItemWithPermission>

      <ConfirmDeleteDialog
        variant={variant}
        isOpen={isConfirmOpen}
        onDelete={handleConfirmDelete}
        onClose={handleClose}
        headerText={confirmDeleteDialog.header}
        message={confirmDeleteDialog.message}
        confirmButtonLabel={confirmDeleteDialog.delete ?? t("GridDrawer.Menu.DeleteItem")}
        errorMessage={confirmDeleteDialog.errorMessage}
        id="confirm-delete"
        /** See more: https://stackoverflow.com/a/56285545 */
        onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
      />
    </>
  );
};
